import React, { useEffect, useRef, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';

function formatTime(value) {
  if (!Number.isFinite(value)) return '0:00';

  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

export default function AudioPlayer({ src, title }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [src]);

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      await audio.play();
    } else {
      audio.pause();
    }
  };

  const seek = (event) => {
    const audio = audioRef.current;
    const nextTime = Number(event.target.value);

    if (audio) {
      audio.currentTime = nextTime;
      setCurrentTime(nextTime);
    }
  };

  const skip = (seconds) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.min(
      Math.max(audio.currentTime + seconds, 0),
      audio.duration || 0
    );
  };

  return (
    <div className="rounded-xl bg-dark-green p-5 text-white shadow-lg">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
      />

      <div className="mb-5 flex items-center gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber text-white shadow-md">
          <SafeIcon name="Headphones" className="text-xl" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-tan">
            Now Playing
          </p>
          <h3 className="truncate font-bold">{title}</h3>
        </div>
      </div>

      <input
        type="range"
        min="0"
        max={duration || 0}
        step="0.1"
        value={currentTime}
        onChange={seek}
        aria-label={`Seek through ${title}`}
        className="h-2 w-full cursor-pointer accent-amber"
      />

      <div className="mt-2 flex justify-between text-xs text-tan">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      <div className="mt-4 flex items-center justify-center gap-5">
        <button
          type="button"
          onClick={() => skip(-15)}
          aria-label="Go back 15 seconds"
          className="rounded-full p-2 text-tan transition hover:bg-white/10 hover:text-white"
        >
          <SafeIcon name="RotateCcw" className="text-xl" />
        </button>

        <button
          type="button"
          onClick={togglePlayback}
          aria-label={playing ? 'Pause episode' : 'Play episode'}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-amber text-white shadow-md transition hover:scale-105 hover:bg-orange-600"
        >
          <SafeIcon name={playing ? 'Pause' : 'Play'} className="text-2xl" />
        </button>

        <button
          type="button"
          onClick={() => skip(15)}
          aria-label="Skip forward 15 seconds"
          className="rounded-full p-2 text-tan transition hover:bg-white/10 hover:text-white"
        >
          <SafeIcon name="RotateCw" className="text-xl" />
        </button>
      </div>
    </div>
  );
}